// =============================================================================
// SIREPPAR - Modal "Informe al TEP" (espejo del Informe al Secretario)
// =============================================================================
// Al elevar los atributos al Tribunal Electoral Provincial (TEP), el Secretario
// redacta el informe (texto editable, con las oposiciones si las hubo).
//  - "Generar borrador": genera y descarga el PDF sin persistir (regenerable).
//  - "Elevar al TEP": genera, descarga, guarda el informe como Documento del
//    expediente y eleva los atributos al TEP (fase ATRIBUTOS_PUBLICADOS →
//    ATRIBUTOS_EN_TEP).
//
// Al pie hay un check para avisarle al partido que el expediente avanzó. Arranca
// DESTILDADO: el informe al TEP es documentación interna y no se le adjunta.
// =============================================================================
import { useEffect, useState } from 'react';
import toast from 'react-hot-toast';
import Modal from './Modal';
import RichTextEditor from './RichTextEditor';
import { api } from '../../services/api';
import PanelHistorialInforme from './PanelHistorialInforme';
import { LABELS_ATRIBUTOS as LABELS, aplicarObjetados } from '../../utils/objecionAtributos';
import { aplicarAntecedentes, ItemHistorialInforme } from '../../utils/historialInforme';
import { aplicarVerificacionDocumental } from '../../utils/verificacionDocumental';
import { conEncabezadoInforme } from '../../utils/informeEncabezado';
import AdjuntosInforme from './AdjuntosInforme';

interface Props {
  // Bloque de advertencia sobre documentación obligatoria rechazada o faltante.
  // Se agrega al final del cuerpo (bloque gestionado) para que la supervisora
  // pueda informarlo aunque el trámite avance. '' = no hay nada que informar.
  advertenciaVerificacion?: string;
  // Denominación del partido: va al encabezado EDITABLE del cuerpo (el PDF ya no
  // la imprime fija, así se puede corregir desde el modal).
  nombrePartido?: string;
  isOpen: boolean;
  onClose: () => void;
  expedienteId: string;
  atributosDisponibles: string[];
  onElevado?: () => void;
}

// Texto formal precargado (editable) según haya o no oposiciones. El PDF ya imprime
// el destinatario y lista todos los atributos + adjunta los símbolos, así que acá va
// solo el cuerpo. Admite formato (negrita/cursiva) vía Markdown.
const TEXTO_SIN_OPOSICION = `Se eleva al Tribunal Electoral Provincial (TEP) el presente informe respecto de la reserva de atributos de la agrupación en trámite de reconocimiento, cuya publicación se efectuó conforme al Art. 10 de la Ley XII N° 23.

Los atributos del partido (denominación, sigla, colores y símbolo) se detallan al pie del presente.

Vencido el plazo legal de cinco (5) días corridos, **no se presentaron oposiciones ni observaciones**.

Los archivos de los símbolos se adjuntan al final del presente informe.

En virtud de lo expuesto, se solicita al Tribunal Electoral Provincial se sirva resolver sobre la procedencia de la reserva de atributos, conforme a derecho.`;

const TEXTO_CON_OPOSICION = `Se eleva al Tribunal Electoral Provincial (TEP) el presente informe respecto de la reserva de atributos de la agrupación en trámite de reconocimiento, cuya publicación se efectuó conforme al Art. 10 de la Ley XII N° 23.

Los atributos del partido (denominación, sigla, colores y símbolo) se detallan al pie del presente.

Vencido el plazo legal de cinco (5) días corridos, **se presentaron oposiciones** respecto de los atributos que se detallan a continuación.

Los archivos de los símbolos se adjuntan al final del presente informe.

En virtud de lo expuesto, se solicita al Tribunal Electoral Provincial se sirva resolver sobre la procedencia de la reserva de atributos, conforme a derecho.`;

export default function ModalInformeTep({ isOpen, onClose, expedienteId, atributosDisponibles, onElevado, advertenciaVerificacion, nombrePartido }: Props) {
  const [texto, setTexto] = useState(TEXTO_SIN_OPOSICION);
  // Si el usuario editó el texto, no lo pisamos al cambiar de "sin/con oposición".
  const [textoTocado, setTextoTocado] = useState(false);
  const [conOposicion, setConOposicion] = useState(false);
  const [atributosEnConflicto, setAtributosEnConflicto] = useState<string[]>([]);
  const [generando, setGenerando] = useState(false);
  // Aviso al partido: destildado por defecto (mismo criterio que los demás informes
  // de elevación). Se manda al endpoint como `notificarPartido`.
  const [avisarPartido, setAvisarPartido] = useState(false);
  // Respaldo de lo que se informa (oposiciones recibidas, notas). Se anexan como
  // hojas al final del PDF del informe; los PDF además van al correo si se notifica.
  const [adjuntos, setAdjuntos] = useState<File[]>([]);

  // La advertencia es un bloque gestionado: se reaplica cada vez que se reemplaza
  // el cuerpo (al cambiar sin/con objeción se pisa el texto entero), así no se
  // pierde. Es idempotente.
  const conAdvertencia = (texto: string) =>
    conEncabezadoInforme(
      aplicarVerificacionDocumental(texto, advertenciaVerificacion ?? ''),
      { destinatario: 'TEP', nombrePartido },
    );

  // Antecedentes tildados en el panel lateral: se vuelcan como bloque al final
  // del cuerpo, sin pisar el bloque de atributos con oposición.
  const aplicarSeleccionHistorial = (items: ItemHistorialInforme[]) => {
    setTexto((cuerpo) => aplicarAntecedentes(items, cuerpo));
  };

  const cambiarOposicion = (valor: boolean) => {
    setConOposicion(valor);
    if (!valor) {
      // Sin oposición: se limpian los atributos marcados y se quita el bloque del cuerpo.
      setAtributosEnConflicto([]);
      setTexto((c) => (textoTocado ? aplicarObjetados([], c) : conAdvertencia(TEXTO_SIN_OPOSICION)));
    } else if (!textoTocado) {
      setTexto(conAdvertencia(TEXTO_CON_OPOSICION));
    }
  };

  const toggleAtributo = (attr: string) => {
    setAtributosEnConflicto((prev) => {
      const yaEsta = prev.includes(attr);
      let next: string[];
      if (attr === 'DENOMINACION' && !yaEsta) {
        next = Array.from(new Set([...prev, ...atributosDisponibles]));
      } else {
        next = yaEsta ? prev.filter((a) => a !== attr) : [...prev, attr];
      }
      const ordenados = atributosDisponibles.filter((a) => next.includes(a));
      setTexto((cuerpo) => aplicarObjetados(ordenados, cuerpo));
      return ordenados;
    });
  };

  // Al abrir (o si la advertencia llega después de las queries) se refleja en el cuerpo.
  useEffect(() => {
    if (!isOpen) return;
    setTexto((cuerpo) => conAdvertencia(cuerpo));
  }, [isOpen, advertenciaVerificacion]);

  // Cada vez que se abre el modal, el check vuelve a destildarse: notificar al
  // partido tiene que ser una decisión explícita en cada elevación.
  useEffect(() => {
    if (isOpen) { setAvisarPartido(false); setAdjuntos([]); }
  }, [isOpen]);

  const descargarPdf = (pdfBase64: string, filename: string) => {
    const bytes = Uint8Array.from(atob(pdfBase64), (c) => c.charCodeAt(0));
    const blob = new Blob([bytes], { type: 'application/pdf' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = filename || 'informe-tep.pdf';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  };

  const generar = async (persistir: boolean) => {
    if (!texto.trim()) {
      toast.error('Redactá el informe al TEP');
      return;
    }
    if (conOposicion && atributosEnConflicto.length === 0) {
      toast.error('Marcá al menos un atributo con oposición');
      return;
    }
    if (persistir) {
      if (!window.confirm('¿Elevar los atributos al TEP? Se guardará el informe y el trámite pasará a resolución del Tribunal.')) {
        return;
      }
    }
    setGenerando(true);
    try {
      // Multipart: el endpoint acepta adjuntos de respaldo.
      const fd = new FormData();
      fd.append('texto', texto.trim());
      fd.append('persistir', String(persistir));
      fd.append('notificarPartido', String(avisarPartido));
      adjuntos.forEach((f) => fd.append('adjuntos', f));
      const res = await api.post(`/reconocimiento/${expedienteId}/informe-tep`, fd, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      descargarPdf(res.data.data.pdfBase64, res.data.data.filename);
      toast.success(persistir ? 'Atributos elevados al TEP' : 'Borrador generado');
      if (persistir) {
        onElevado?.();
        onClose();
      }
    } catch (err: any) {
      toast.error(err.response?.data?.error?.message || 'Error al generar el informe');
    } finally {
      setGenerando(false);
    }
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose} title="Informe al TEP / Elevación de atributos" size="full">
      <div className="space-y-5">
        <p className="text-sm text-gray-600 bg-gray-50 rounded-lg p-4">
          Redacte el informe que se eleva al Tribunal Electoral Provincial. Indique si hubo
          oposiciones durante el plazo de publicación y, en su caso, marque los atributos
          objetados (se listan en el cuerpo). Puede dar formato al texto.
        </p>

        <div className="flex gap-3">
          <button
            type="button"
            onClick={() => cambiarOposicion(false)}
            className={`flex-1 px-4 py-3 rounded-lg border text-sm font-medium transition-colors ${
              !conOposicion ? 'bg-green-600 text-white border-green-600' : 'bg-white text-gray-700 border-gray-300'
            }`}
          >
            Sin oposiciones
          </button>
          <button
            type="button"
            onClick={() => cambiarOposicion(true)}
            className={`flex-1 px-4 py-3 rounded-lg border text-sm font-medium transition-colors ${
              conOposicion ? 'bg-amber-600 text-white border-amber-600' : 'bg-white text-gray-700 border-gray-300'
            }`}
          >
            Con oposiciones
          </button>
        </div>

        {conOposicion && (
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Atributos con oposición
            </label>
            <div className="space-y-2">
              {atributosDisponibles.map((attr) => (
                <label key={attr} className="flex items-center gap-2 text-sm cursor-pointer">
                  <input
                    type="checkbox"
                    checked={atributosEnConflicto.includes(attr)}
                    onChange={() => toggleAtributo(attr)}
                    className="w-4 h-4"
                  />
                  {LABELS[attr] || attr}
                </label>
              ))}
            </div>
            <p className="text-xs text-gray-400 mt-2">
              Al marcar atributos se agregan al final del cuerpo del informe. La redacción de las
              oposiciones va en el cuerpo.
            </p>
          </div>
        )}

        <div className="grid grid-cols-1 lg:grid-cols-[1fr_20rem] gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Informe al TEP
            </label>
            <RichTextEditor
              value={texto}
              onChange={(v) => { setTexto(v ?? ''); setTextoTocado(true); }}
              minHeight={280}
              placeholder="Fundamentos del informe: resultado de la publicación, oposiciones presentadas y su detalle, y lo que se solicita al Tribunal…"
            />
            <p className="text-xs text-gray-400 mt-1">
              El PDF lista siempre todos los atributos del partido y adjunta los símbolos, más allá de este texto.
            </p>
          </div>
          {isOpen && (
            <PanelHistorialInforme
              expedienteId={expedienteId}
              resetKey={isOpen}
              onSeleccion={aplicarSeleccionHistorial}
            />
          )}
        </div>

        <AdjuntosInforme
          archivos={adjuntos}
          onChange={setAdjuntos}
          destacado={conOposicion}
          ayuda={conOposicion
            ? 'Adjuntá lo que respalda las oposiciones: captura de pantalla, el correo recibido o la nota escaneada en PDF.'
            : undefined}
        />

        <label className="flex items-start gap-2 text-sm text-gray-700 bg-blue-50 border border-blue-100 rounded-lg p-3 cursor-pointer">
          <input
            type="checkbox"
            className="mt-0.5 w-4 h-4"
            checked={avisarPartido}
            onChange={(e) => setAvisarPartido(e.target.checked)}
          />
          <span>
            Avisar al partido por correo que el expediente avanzó
            <span className="block text-xs text-gray-500">
              El informe NO se adjunta: es documentación interna del organismo. Si no lo tildás,
              no se le notifica nada al partido.
            </span>
          </span>
        </label>

        <div className="flex gap-3 pt-2">
          <button
            type="button"
            onClick={() => generar(false)}
            disabled={generando}
            className="flex-1 px-4 py-2.5 bg-blue-600 text-white text-sm font-medium rounded-lg hover:bg-blue-700 disabled:opacity-50 transition-colors"
          >
            {generando ? 'Generando…' : '📄 Generar borrador'}
          </button>
          <button
            type="button"
            onClick={() => generar(true)}
            disabled={generando}
            className="flex-1 px-4 py-2.5 bg-indigo-600 text-white text-sm font-medium rounded-lg hover:bg-indigo-700 disabled:opacity-50 transition-colors"
          >
            {generando ? 'Procesando…' : '✅ Elevar al TEP'}
          </button>
        </div>
      </div>
    </Modal>
  );
}
